import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SHARE_KINDS, SHARE_KIND_LABELS, type AccountView, type AreaView, type ClientView, type CommissionStatementView, type Deal, type DealRule, type DealView, type LevelView, type ShareKind } from '@lp/contracts';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { del, errorText, fieldProblems, get, patch, post, put } from '@/lib/api';
import { useScope } from '@/lib/scope';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select, Tabs, cn } from '@/components/ui';

type Tab = 'accounts' | 'levels' | 'areas' | 'commission';
const today = () => new Date().toISOString().slice(0, 10);

/**
 * A company's hierarchy (master prompt §11–12): its own levels, its accounts (one parent each, never a loop), its areas (many per
 * account), each account's deal, and the commission statement — every figure from the server's one commission engine.
 */
export function ClientHierarchyPage() {
  const t = useT();
  const scope = useScope();
  const id = scope.clientId;
  const [tab, setTab] = useState<Tab>(scope.can('accounts', 'read') ? 'accounts' : 'commission');
  const client = useQuery({ queryKey: ['client', id], queryFn: () => get<ClientView>(`/platform/clients/${id}`), enabled: !scope.company });
  return (
    <>
      {!scope.company && (
        <Link to={`/clients/${id}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
          <ArrowLeft size={12} /> {client.data?.nameEn ?? t('Client')}
        </Link>
      )}
      <PageHeader title={t('Hierarchy and commission')} subtitle={t('Levels named by the company, accounts under them, their areas and what each account earns.')} />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        items={[
          ...(scope.can('accounts', 'read') ? [{ value: 'accounts' as Tab, label: t('Accounts') }, { value: 'levels' as Tab, label: t('Levels') }, { value: 'areas' as Tab, label: t('Areas') }] : []),
          ...(scope.can('reports', 'read') ? [{ value: 'commission' as Tab, label: t('Commission') }] : []),
        ]}
      />
      {tab === 'accounts' && <Accounts clientId={id} />}
      {tab === 'levels' && <Levels clientId={id} />}
      {tab === 'areas' && <Areas clientId={id} />}
      {tab === 'commission' && <Statement clientId={id} />}
    </>
  );
}

const useLevels = (clientId: number) => {
  const { base } = useScope();
  return useQuery({ queryKey: ['client', clientId, 'levels'], queryFn: () => get<LevelView[]>(`${base}/levels`) });
};
const useAreas = (clientId: number) => {
  const { base } = useScope();
  return useQuery({ queryKey: ['client', clientId, 'areas'], queryFn: () => get<AreaView[]>(`${base}/areas`) });
};
export const useAccounts = (clientId: number, enabled = true) => {
  const { base } = useScope();
  return useQuery({ queryKey: ['client', clientId, 'accounts'], queryFn: () => get<AccountView[]>(`${base}/accounts`), enabled });
};

// ------------------------------------------------------------------ accounts
function Accounts({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const superAdmin = scope.can('accounts', 'edit');
  const canDeal = scope.can('deals', 'read');
  const list = useAccounts(clientId);
  const levels = useLevels(clientId);
  const [adding, setAdding] = useState<{ parent: AccountView | null } | null>(null);
  const [dealOf, setDealOf] = useState<AccountView | null>(null);
  const [editing, setEditing] = useState<AccountView | null>(null);
  const status = useMutation({
    mutationFn: (a: AccountView) => patch<AccountView[]>(`${scope.base}/accounts/${a.id}`, { status: a.status === 'active' ? 'disabled' : 'active' }),
    onSuccess: (l) => qc.setQueryData(['client', clientId, 'accounts'], l),
  });
  const canTop = scope.companyLevel && (levels.data ?? []).some((l) => l.allowedUnder.includes(null));
  const own = scope.company?.account?.id ?? null;
  return (
    <Card
      title={t('Accounts')}
      actions={
        superAdmin &&
        canTop && (
          <Button variant="secondary" icon={<Plus size={14} />} onClick={() => setAdding({ parent: null })}>
            {t('Account under the company')}
          </Button>
        )
      }
    >
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <ErrorBox text={errorText(list.error)} />
      ) : !levels.data?.length ? (
        <p className="text-sm text-ink-600">{t('Make the company’s levels first (Levels).')}</p>
      ) : !list.data?.length ? (
        <p className="text-sm text-ink-600">{t('No account yet.')}</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {list.data.map((a) => {
              const childLevels = (levels.data ?? []).filter((l) => l.allowedUnder.includes(a.levelCode));
              return (
                <tr key={a.id} className={cn('border-b border-ink-100 last:border-0', a.status !== 'active' && 'opacity-50')} data-testid={`account-${a.code}`}>
                  <td className="py-2 pr-3" style={{ paddingLeft: `${a.depth * 20}px` }}>
                    {a.depth > 0 && <span className="mr-1 text-ink-300">└</span>}
                    <span className="font-medium">{a.name}</span> <span className="font-mono text-[11px] text-ink-500">{a.code}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <Badge tone={a.canSell ? 'green' : 'gray'}>{a.levelName}</Badge>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-ink-500">{a.areas.join(' ')}</td>
                  <td className="py-2 text-right">
                    {(superAdmin || canDeal) && (
                      <span className="inline-flex gap-1">
                        {canDeal && (
                          <Button variant="ghost" onClick={() => setDealOf(a)}>
                            {t('Deal')}
                          </Button>
                        )}
                        {superAdmin && a.id !== own && (
                          <Button variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(a)}>
                            {t('Edit')}
                          </Button>
                        )}
                        {superAdmin && childLevels.length > 0 && a.status === 'active' && (
                          <Button variant="ghost" icon={<Plus size={12} />} onClick={() => setAdding({ parent: a })}>
                            {t('Under it')}
                          </Button>
                        )}
                        {superAdmin && a.id !== own && (
                          <Button variant="ghost" onClick={() => status.mutate(a)} disabled={status.isPending}>
                            {t(a.status === 'active' ? 'Disable' : 'Enable')}
                          </Button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {status.error && <ErrorBox text={errorText(status.error)} />}
      {adding && levels.data && <NewAccount clientId={clientId} parent={adding.parent} levels={levels.data} onClose={() => setAdding(null)} />}
      {dealOf && <DealDialog clientId={clientId} account={dealOf} onClose={() => setDealOf(null)} />}
      {editing && list.data && <EditAccount clientId={clientId} account={editing} all={list.data} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function NewAccount({ clientId, parent, levels, onClose }: { clientId: number; parent: AccountView | null; levels: LevelView[]; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const areas = useAreas(clientId);
  const allowed = levels.filter((l) => l.allowedUnder.includes(parent?.levelCode ?? null));
  const [f, setF] = useState({ code: '', name: '', levelCode: allowed[0]?.code ?? '', areaCodes: [] as string[] });
  const create = useMutation({
    mutationFn: () => post<AccountView[]>(`${scope.base}/accounts`, { ...f, parentId: parent?.id ?? null }),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'accounts'], l);
      void qc.invalidateQueries({ queryKey: ['client', clientId, 'entitlements'] });
      onClose();
    },
  });
  return (
    <Dialog
      title={`${t('New account')} — ${parent ? parent.name : t('under the company')}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => create.mutate()} disabled={!f.code || !f.name || !f.levelCode || create.isPending}>
            {t('Create')}
          </Button>
        </>
      }
    >
      <Field label={t('Level')}>
        <Select value={f.levelCode} onChange={(e) => setF({ ...f, levelCode: e.target.value })} className="w-full" aria-label={t('Level')}>
          {allowed.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nameEn} ({l.code})
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('Code')}>
        <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} maxLength={20} />
      </Field>
      <Field label={t('Name')}>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} />
      </Field>
      {!!areas.data?.length && (
        <Field label={t('Areas')}>
          <div className="flex flex-wrap gap-2">
            {areas.data.map((a) => (
              <label key={a.code} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={f.areaCodes.includes(a.code)} onChange={(e) => setF({ ...f, areaCodes: e.target.checked ? [...f.areaCodes, a.code] : f.areaCodes.filter((c) => c !== a.code) })} />
                {a.nameEn}
              </label>
            ))}
          </div>
        </Field>
      )}
      {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
    </Dialog>
  );
}

/** an account changed: its code, name, parent (never under itself — the server checks the levels) and areas */
function EditAccount({ clientId, account, all, onClose }: { clientId: number; account: AccountView; all: AccountView[]; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const areas = useAreas(clientId);
  const [f, setF] = useState({ code: account.code, name: account.name, parentId: account.parentId, areaCodes: account.areas });
  // not itself, nor anything under it
  const below = new Set<number>([account.id]);
  for (const a of all) if (a.parentId != null && below.has(a.parentId)) below.add(a.id);
  const save = useMutation({
    mutationFn: () =>
      patch<AccountView[]>(`${scope.base}/accounts/${account.id}`, {
        ...(f.code !== account.code ? { code: f.code } : {}),
        ...(f.name !== account.name ? { name: f.name } : {}),
        ...(f.parentId !== account.parentId ? { parentId: f.parentId } : {}),
        ...(f.areaCodes.join() !== account.areas.join() ? { areaCodes: f.areaCodes } : {}),
      }),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'accounts'], l);
      onClose();
    },
  });
  const changed = f.code !== account.code || f.name !== account.name || f.parentId !== account.parentId || f.areaCodes.join() !== account.areas.join();
  return (
    <Dialog
      title={`${t('Edit')} — ${account.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!changed || !f.code || !f.name || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Code')}>
        <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} maxLength={20} />
      </Field>
      <Field label={t('Name')}>
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} />
      </Field>
      {scope.companyLevel && (
        <Field label={t('Under')}>
          <Select value={f.parentId ?? ''} onChange={(e) => setF({ ...f, parentId: e.target.value ? Number(e.target.value) : null })} className="w-full" aria-label={t('Under')}>
            <option value="">{t('the company')}</option>
            {all
              .filter((a) => !below.has(a.id))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.code}) — {a.levelName}
                </option>
              ))}
          </Select>
        </Field>
      )}
      {!!areas.data?.length && (
        <Field label={t('Areas')}>
          <div className="flex flex-wrap gap-2">
            {areas.data.map((a) => (
              <label key={a.code} className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={f.areaCodes.includes(a.code)} onChange={(e) => setF({ ...f, areaCodes: e.target.checked ? [...f.areaCodes, a.code] : f.areaCodes.filter((c) => c !== a.code) })} />
                {a.nameEn}
              </label>
            ))}
          </div>
        </Field>
      )}
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

/** an account's deal, with what its parent has beside it — the server refuses more than the parent has */
function DealDialog({ clientId, account, onClose }: { clientId: number; account: AccountView; onClose: () => void }) {
  const t = useT();
  const scope = useScope();
  const canEdit = scope.can('deals', 'edit') && account.id !== scope.company?.account?.id;
  const view = useQuery({ queryKey: ['client', clientId, 'deal', account.id], queryFn: () => get<DealView>(`${scope.base}/accounts/${account.id}/deal`) });
  const [deal, setDeal] = useState<Deal | null>(null);
  const d = deal ?? view.data?.deal ?? null;
  const save = useMutation({ mutationFn: () => put<DealView>(`${scope.base}/accounts/${account.id}/deal`, d), onSuccess: onClose });
  const setRule = (i: number, r: Partial<DealRule>) => setDeal({ ...d!, rules: d!.rules.map((x, j) => (j === i ? { ...x, ...r } : x)) });
  return (
    <Dialog
      title={`${t('Deal')} — ${account.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canEdit || !deal || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      {!d || !view.data ? (
        <Loading />
      ) : (
        <>
          <p className="text-xs text-ink-500">
            {t('The parent has')} ({view.data.parent.code ?? t('the company')}): {view.data.parent.deal.rules.map((r) => `${[r.product || t('every product'), r.game, r.digits && `${r.digits}D`].filter(Boolean).join(' ')} ${r.commission}%${r.share ? ` / ${r.share}%` : ''}`).join(' · ') || '—'}
          </p>
          <Field label={t('Share kind')}>
            <Select value={d.shareKind} onChange={(e) => setDeal({ ...d, shareKind: e.target.value as ShareKind })} className="w-full">
              {SHARE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(SHARE_KIND_LABELS[k])}
                </option>
              ))}
            </Select>
          </Field>
          <table className="w-full text-xs" data-testid="deal-rules">
            <thead>
              <tr className="text-left text-ink-500">
                <th>{t('Product')}</th>
                <th>{t('Game')}</th>
                <th>2D/3D</th>
                <th>{t('Commission %')}</th>
                <th>{t('Share %')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.rules.map((r, i) => (
                <tr key={i}>
                  <td>
                    <Input value={r.product} onChange={(e) => setRule(i, { product: e.target.value.toUpperCase() })} placeholder={t('all')} className="h-7 w-16 px-1 text-xs" />
                  </td>
                  <td>
                    <Input value={r.game} onChange={(e) => setRule(i, { game: e.target.value.toUpperCase() })} placeholder={t('all')} className="h-7 w-20 px-1 text-xs" />
                  </td>
                  <td>
                    <Select value={r.digits} onChange={(e) => setRule(i, { digits: e.target.value as DealRule['digits'] })} className="h-7 px-1 text-xs">
                      <option value="">{t('all')}</option>
                      <option value="2">2D</option>
                      <option value="3">3D</option>
                    </Select>
                  </td>
                  <td>
                    <Input type="number" value={r.commission} onChange={(e) => setRule(i, { commission: Number(e.target.value) })} className="h-7 w-16 px-1 text-xs" aria-label={t('Commission %')} />
                  </td>
                  <td>
                    <Input type="number" value={r.share} onChange={(e) => setRule(i, { share: Number(e.target.value) })} disabled={d.shareKind === 'none'} className="h-7 w-16 px-1 text-xs" aria-label={t('Share %')} />
                  </td>
                  <td>
                    <Button variant="ghost" icon={<Trash2 size={12} />} onClick={() => setDeal({ ...d, rules: d.rules.filter((_, j) => j !== i) })} className="h-7 px-1" aria-label={t('Remove')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button variant="secondary" icon={<Plus size={12} />} onClick={() => setDeal({ ...d, rules: [...d.rules, { product: '', game: '', digits: '', commission: 0, share: 0 }] })}>
            {t('Add a rule')}
          </Button>
          <p className="text-[11px] text-ink-500">{t('Empty = every one. The most specific rule wins. Profit share: sell − winning − commission; winning share: only when there are winnings.')}</p>
          {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
        </>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------ levels
function Levels({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const superAdmin = scope.can('accounts', 'edit') && scope.companyLevel;
  const levels = useLevels(clientId);
  const [f, setF] = useState({ code: '', nameEn: '', nameKm: '', canSell: false, allowedUnder: [null] as (string | null)[] });
  const create = useMutation({
    mutationFn: () => post<LevelView[]>(`${scope.base}/levels`, f),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'levels'], l);
      setF({ code: '', nameEn: '', nameKm: '', canSell: false, allowedUnder: [null] });
    },
  });
  const toggle = (u: string | null) => setF({ ...f, allowedUnder: f.allowedUnder.includes(u) ? f.allowedUnder.filter((x) => x !== u) : [...f.allowedUnder, u] });
  const [editing, setEditing] = useState<LevelView | null>(null);
  const remove = useMutation({ mutationFn: (l: LevelView) => del<LevelView[]>(`${scope.base}/levels/${l.id}`), onSuccess: (l) => l && qc.setQueryData(['client', clientId, 'levels'], l) });
  return (
    <Card title={t('Levels')}>
      {editing && levels.data && <EditLevel clientId={clientId} level={editing} levels={levels.data} onClose={() => setEditing(null)} />}
      {remove.error && <ErrorBox text={errorText(remove.error)} />}
      {levels.isLoading ? (
        <Loading />
      ) : (
        <table className="mb-3 w-full text-sm">
          <tbody>
            {(levels.data ?? []).map((l) => (
              <tr key={l.id} className="border-b border-ink-100 last:border-0" data-testid={`level-${l.code}`}>
                <td className="py-2 pr-3">
                  <span className="font-medium">{l.nameEn}</span> <span className="text-xs text-ink-500">{l.nameKm}</span> <span className="font-mono text-[11px] text-ink-500">{l.code}</span>
                </td>
                <td className="py-2 pr-3 text-xs">
                  {t('Under')}: {l.allowedUnder.map((u) => u ?? t('the company')).join(', ')}
                </td>
                <td className="py-2 pr-3">{l.canSell && <Badge tone="green">{t('Sells')}</Badge>}</td>
                <td className="py-2 text-right tabular-nums text-xs text-ink-500">
                  {l.accounts} {t('accounts')}
                </td>
                {superAdmin && (
                  <td className="py-2 pl-2 text-right whitespace-nowrap">
                    <Button variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(l)} aria-label={`${t('Edit')} ${l.code}`} />
                    {l.accounts === 0 && <Button variant="ghost" icon={<Trash2 size={12} />} onClick={() => window.confirm(`${t('Delete')} ${l.code}?`) && remove.mutate(l)} aria-label={`${t('Delete')} ${l.code}`} />}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {superAdmin && (
        <div className="space-y-2 border-t border-ink-100 pt-3">
          <div className="flex flex-wrap gap-2">
            <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder={t('Code')} className="w-28" maxLength={20} aria-label={t('Code')} />
            <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} placeholder={t('Name (English)')} className="w-44" aria-label={t('Name (English)')} />
            <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} placeholder={t('Name (Khmer)')} className="w-44" aria-label={t('Name (Khmer)')} />
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={f.canSell} onChange={(e) => setF({ ...f, canSell: e.target.checked })} />
              {t('Sells')}
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="text-ink-500">{t('May sit under')}:</span>
            {[null, ...(levels.data ?? []).map((l) => l.code)].map((u) => (
              <label key={u ?? '-'} className="flex items-center gap-1">
                <input type="checkbox" checked={f.allowedUnder.includes(u)} onChange={() => toggle(u)} />
                {u ?? t('the company')}
              </label>
            ))}
          </div>
          <Button icon={<Plus size={14} />} onClick={() => create.mutate()} disabled={!f.code || !f.nameEn || !f.nameKm || !f.allowedUnder.length || create.isPending}>
            {t('Add the level')}
          </Button>
          {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ areas
function Areas({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const superAdmin = scope.can('accounts', 'edit') && scope.companyLevel;
  const areas = useAreas(clientId);
  const [f, setF] = useState({ code: '', nameEn: '', nameKm: '', parentCode: '' });
  const create = useMutation({
    mutationFn: () => post<AreaView[]>(`${scope.base}/areas`, { ...f, parentCode: f.parentCode || null }),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'areas'], l);
      setF({ code: '', nameEn: '', nameKm: '', parentCode: '' });
    },
  });
  const [editing, setEditing] = useState<AreaView | null>(null);
  const remove = useMutation({ mutationFn: (a: AreaView) => del<AreaView[]>(`${scope.base}/areas/${a.id}`), onSuccess: (l) => l && qc.setQueryData(['client', clientId, 'areas'], l) });
  return (
    <Card title={t('Areas')}>
      {editing && areas.data && <EditArea clientId={clientId} area={editing} areas={areas.data} onClose={() => setEditing(null)} />}
      {remove.error && <ErrorBox text={errorText(remove.error)} />}
      <p className="mb-2 text-xs text-ink-500">{t('Geography is separate from the hierarchy: one account may cover many areas.')}</p>
      <table className="mb-3 w-full text-sm">
        <tbody>
          {(areas.data ?? []).map((a) => (
            <tr key={a.code} className="border-b border-ink-100 last:border-0">
              <td className="py-1.5 pr-3">
                {a.parentCode && <span className="mr-1 text-ink-300">└</span>}
                {a.nameEn} <span className="text-xs text-ink-500">{a.nameKm}</span> <span className="font-mono text-[11px] text-ink-500">{a.code}</span>
              </td>
              <td className="py-1.5 pr-3 text-xs text-ink-500">{a.parentCode ?? ''}</td>
              <td className="py-1.5 text-right text-xs text-ink-500">
                {a.accounts} {t('accounts')}
              </td>
              {superAdmin && (
                <td className="py-1.5 pl-2 text-right whitespace-nowrap">
                  <Button variant="ghost" icon={<Pencil size={12} />} onClick={() => setEditing(a)} aria-label={`${t('Edit')} ${a.code}`} />
                  <Button variant="ghost" icon={<Trash2 size={12} />} onClick={() => window.confirm(`${t('Delete')} ${a.code}?`) && remove.mutate(a)} aria-label={`${t('Delete')} ${a.code}`} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {superAdmin && (
        <div className="flex flex-wrap gap-2 border-t border-ink-100 pt-3">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder={t('Code')} className="w-24" />
          <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} placeholder={t('Name (English)')} className="w-40" />
          <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} placeholder={t('Name (Khmer)')} className="w-40" />
          <Select value={f.parentCode} onChange={(e) => setF({ ...f, parentCode: e.target.value })} aria-label={t('Inside')}>
            <option value="">{t('Top area')}</option>
            {(areas.data ?? []).map((a) => (
              <option key={a.code} value={a.code}>
                {a.nameEn}
              </option>
            ))}
          </Select>
          <Button icon={<Plus size={14} />} onClick={() => create.mutate()} disabled={!f.code || !f.nameEn || !f.nameKm || create.isPending}>
            {t('Add')}
          </Button>
          {create.error && <ErrorBox text={errorText(create.error)} />}
        </div>
      )}
    </Card>
  );
}

function EditLevel({ clientId, level, levels, onClose }: { clientId: number; level: LevelView; levels: LevelView[]; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const [f, setF] = useState({ nameEn: level.nameEn, nameKm: level.nameKm, canSell: level.canSell, allowedUnder: level.allowedUnder });
  const save = useMutation({
    mutationFn: () => patch<LevelView[]>(`${scope.base}/levels/${level.id}`, f),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'levels'], l);
      void qc.invalidateQueries({ queryKey: ['client', clientId, 'accounts'] });
      onClose();
    },
  });
  const toggle = (u: string | null) => setF({ ...f, allowedUnder: f.allowedUnder.includes(u) ? f.allowedUnder.filter((x) => x !== u) : [...f.allowedUnder, u] });
  return (
    <Dialog
      title={`${t('Edit')} — ${level.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!f.nameEn || !f.nameKm || !f.allowedUnder.length || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Name (English)')}>
        <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Name (Khmer)')}>
        <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} maxLength={80} />
      </Field>
      <label className="flex items-center gap-1.5 text-sm">
        <input type="checkbox" checked={f.canSell} onChange={(e) => setF({ ...f, canSell: e.target.checked })} />
        {t('Sells')}
      </label>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-ink-500">{t('May sit under')}:</span>
        {[null, ...levels.filter((l) => l.id !== level.id).map((l) => l.code)].map((u) => (
          <label key={u ?? '-'} className="flex items-center gap-1">
            <input type="checkbox" checked={f.allowedUnder.includes(u)} onChange={() => toggle(u)} />
            {u ?? t('the company')}
          </label>
        ))}
      </div>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

function EditArea({ clientId, area, areas, onClose }: { clientId: number; area: AreaView; areas: AreaView[]; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  const [f, setF] = useState({ nameEn: area.nameEn, nameKm: area.nameKm, parentCode: area.parentCode ?? '' });
  const save = useMutation({
    mutationFn: () => patch<AreaView[]>(`${scope.base}/areas/${area.id}`, { nameEn: f.nameEn, nameKm: f.nameKm, ...((f.parentCode || null) !== area.parentCode ? { parentCode: f.parentCode || null } : {}) }),
    onSuccess: (l) => {
      qc.setQueryData(['client', clientId, 'areas'], l);
      onClose();
    },
  });
  return (
    <Dialog
      title={`${t('Edit')} — ${area.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!f.nameEn || !f.nameKm || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Name (English)')}>
        <Input value={f.nameEn} onChange={(e) => setF({ ...f, nameEn: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Name (Khmer)')}>
        <Input value={f.nameKm} onChange={(e) => setF({ ...f, nameKm: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Inside')}>
        <Select value={f.parentCode} onChange={(e) => setF({ ...f, parentCode: e.target.value })} className="w-full" aria-label={t('Inside')}>
          <option value="">{t('Top area')}</option>
          {areas
            .filter((a) => a.id !== area.id)
            .map((a) => (
              <option key={a.code} value={a.code}>
                {a.nameEn}
              </option>
            ))}
        </Select>
      </Field>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

// ------------------------------------------------------------------ statement
function Statement({ clientId }: { clientId: number }) {
  const t = useT();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const { base } = useScope();
  const st = useQuery({ queryKey: ['client', clientId, 'commission', from, to], queryFn: () => get<CommissionStatementView>(`${base}/commission?from=${from}&to=${to}`) });
  return (
    <Card
      title={t('Commission statement')}
      actions={
        <span className="flex items-center gap-2 text-xs">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36 text-xs" aria-label={t('From')} />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 text-xs" aria-label={t('To')} />
        </span>
      }
    >
      {st.isLoading ? (
        <Loading />
      ) : st.error || !st.data ? (
        <ErrorBox text={errorText(st.error)} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="statement">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs text-ink-500">
                  <th className="py-1.5 pr-3">{t('Account')}</th>
                  <th className="py-1.5 pr-3 text-right">{t('Sales')}</th>
                  <th className="py-1.5 pr-3 text-right">{t('Winnings')}</th>
                  <th className="py-1.5 pr-3 text-right">{t('Commission')}</th>
                  <th className="py-1.5 pr-3 text-right">{t('Share')}</th>
                </tr>
              </thead>
              <tbody>
                {st.data.rows.map((r) => (
                  <tr key={r.accountId} className="border-b border-ink-100">
                    <td className="py-1.5 pr-3" style={{ paddingLeft: `${r.depth * 16}px` }}>
                      {r.name} <span className="font-mono text-[11px] text-ink-500">{r.code}</span> <span className="text-[11px] text-ink-400">{r.levelName}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{num(r.sales)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{num(r.winnings)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{num(r.commission)}</td>
                    <td className={cn('py-1.5 pr-3 text-right tabular-nums', r.share < 0 && 'text-red-700')}>
                      {r.shareKind === 'none' ? '—' : num(r.share)}
                    </td>
                  </tr>
                ))}
                {st.data.company && (
                <tr className="font-semibold">
                  <td className="py-2 pr-3">{t('The company')}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(st.data.company.sales)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(st.data.company.winnings)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(st.data.company.commission)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(st.data.company.share)}</td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-sm">
            {st.data.company && (
              <>
                {t('The company keeps')}: <span className="font-semibold tabular-nums">{num(st.data.company.keeps)}</span>
              </>
            )}
            {st.data.openTickets > 0 && (
              <span className="ml-2 text-xs text-amber-700">
                ({st.data.openTickets} {t('tickets not settled yet — their winnings are not in')})
              </span>
            )}
          </p>
        </>
      )}
    </Card>
  );
}
